/**
 * Configuration utility that supports both build-time and runtime configuration
 * 
 * Priority:
 * 1. Runtime config from /config.json (can be changed without rebuild)
 * 2. Build-time REACT_APP_API_URL environment variable
 * 3. Auto-detect from window.location (relative path)
 * 4. Fallback to localhost for development
 */

let cachedConfig: { API_URL: string } | null = null;

export async function getConfig(): Promise<{ API_URL: string }> {
  if (cachedConfig) {
    return cachedConfig;
  }

  // Try to load runtime config from public/config.json
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const runtimeConfig = await response.json();
      if (runtimeConfig.API_URL) {
        let apiUrl = runtimeConfig.API_URL;
        const origin = window.location.origin;
        const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');
        
        // Only make relative paths absolute for localhost (development)
        // In production, keep relative paths so Nginx can proxy them
        if (apiUrl.startsWith('/')) {
          if (isLocalhost) {
            // Development: make it absolute with backend port
            apiUrl = `http://localhost:43001${apiUrl}`;
          }
          // Production: keep as relative path (Nginx will proxy)
        } else if (apiUrl.startsWith('http') && !isLocalhost && apiUrl.includes(':43000')) {
          // Production: if config.json has absolute URL with frontend port, convert to relative
          try {
            const urlObj = new URL(apiUrl);
            apiUrl = urlObj.pathname + urlObj.search;
          } catch (e) {
            // Invalid URL, fallback to /api
            apiUrl = '/api';
          }
        }
        cachedConfig = { API_URL: apiUrl };
        console.log('[Config] Loaded from config.json:', apiUrl, { isLocalhost, origin });
        return cachedConfig;
      }
    }
  } catch (error) {
    // config.json not found or invalid, continue to fallbacks
    console.log('[Config] Failed to load config.json:', error);
  }

  // Fallback to build-time environment variable
  const buildTimeUrl = process.env.REACT_APP_API_URL;
  if (buildTimeUrl) {
    cachedConfig = { API_URL: buildTimeUrl };
    return cachedConfig;
  }

  // Auto-detect from window.location (for same-origin deployments)
  // If frontend and backend are on same domain, use relative path
  const origin = window.location.origin;
  const port = window.location.port;
  const hostname = window.location.hostname;
  
  // Always use absolute URL for localhost to avoid nginx serving index.html
  // Check if we're accessing via localhost (common in Docker with port mapping)
  let apiUrl: string;
  
  // Priority: check origin first (most reliable in Docker)
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    // Local development: always use absolute URL with port 43001
    // This prevents nginx from serving index.html for /api/* requests
    apiUrl = `http://localhost:43001/api`;
  } else if (port === '43000' || port === '3000') {
    // Check if this is production (not localhost) - use relative path for Nginx proxy
    // Only use absolute URL if it's actually localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Development: use absolute URL with backend port
      apiUrl = `${origin.replace(/:(43000|3000)$/, ':43001')}/api`;
    } else {
      // Production: use relative path (Nginx will proxy to backend)
      apiUrl = '/api';
    }
  } else if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '') {
    // Local development: always use absolute URL with port 43001
    apiUrl = `http://localhost:43001/api`;
  } else {
    // Production: assume backend is on same origin (relative path)
    apiUrl = '/api';
  }

  cachedConfig = { API_URL: apiUrl };
  console.log('[Config] getConfig() returning:', apiUrl, { origin, port, hostname });
  return cachedConfig;
}

// Synchronous version for immediate use (uses cached config or fallback)
export function getConfigSync(): { API_URL: string } {
  if (cachedConfig) {
    return cachedConfig;
  }

  // Use build-time env var if available
  const buildTimeUrl = process.env.REACT_APP_API_URL;
  if (buildTimeUrl) {
    // If it's a relative path, make it absolute
    let apiUrl = buildTimeUrl;
    if (apiUrl.startsWith('/')) {
      apiUrl = `${window.location.origin}${apiUrl}`;
    }
    cachedConfig = { API_URL: apiUrl };
    return cachedConfig;
  }

  // Auto-detect fallback
  const origin = window.location.origin;
  const port = window.location.port;
  const hostname = window.location.hostname;
  
  // Always use absolute URL for localhost to avoid nginx serving index.html
  // Check if we're accessing via localhost (common in Docker with port mapping)
  let apiUrl: string;
  
  // Priority: check origin first (most reliable in Docker)
  // If origin contains localhost or 127.0.0.1, ALWAYS use absolute URL
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    // Local development: always use absolute URL with port 43001
    // This prevents nginx from serving index.html for /api/* requests
    apiUrl = `http://localhost:43001/api`;
  } else if (port === '43000' || port === '3000') {
    // Check if this is production (not localhost) - use relative path for Nginx proxy
    // Only use absolute URL if it's actually localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Development: use absolute URL with backend port
      apiUrl = `${origin.replace(/:(43000|3000)$/, ':43001')}/api`;
    } else {
      // Production: use relative path (Nginx will proxy to backend)
      apiUrl = '/api';
    }
  } else if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '') {
    // Local development: always use absolute URL with port 43001
    apiUrl = `http://localhost:43001/api`;
  } else {
    // Production: use relative path (same origin)
    apiUrl = '/api';
  }

  // Store in cache for next time
  cachedConfig = { API_URL: apiUrl };
  console.log('[Config] getConfigSync() returning:', apiUrl, { origin, port, hostname, href: window.location.href });
  return cachedConfig;
}
